/**
 * Profile Repository
 * Data access for user profiles
 * 
 * @module repositories/profileRepository
 */

import { supabase } from '../services/supabase';
import { ApiError } from '../lib/errors';
import { logger } from '../lib/logger';

// =============================================================================
// Types
// =============================================================================

export interface ProfileRow {
    id: string;
    full_name: string | null;
    username: string | null;
    avatar_url: string | null;
    email: string | null;
    created_at: string;
    updated_at: string;
}

export interface ProfileUpdate {
    full_name?: string;
    avatar_url?: string;
    email?: string;
    username?: string;
}

export interface ProfileInsert {
    id: string;
    email?: string;
    full_name?: string;
    avatar_url?: string;
    username?: string;
}

// =============================================================================
// Repository
// =============================================================================

export const profileRepository = {
    /**
     * Find profile by user ID
     */
    async findById(userId: string): Promise<ProfileRow | null> {
        logger.debug('[ProfileRepo] findById', { userId });

        const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single();

        if (error) {
            if (error.code === 'PGRST116') {
                return null; // Not found
            }
            throw new ApiError('Failed to fetch profile', {
                statusCode: 500,
                context: { userId, error: error.message }
            });
        }

        return data;
    },

    /**
     * Create profile
     */
    async create(profile: ProfileInsert): Promise<ProfileRow> {
        logger.debug('[ProfileRepo] create', { id: profile.id });

        const { data, error } = await supabase
            .from('profiles')
            .insert(profile)
            .select()
            .single();

        if (error) {
            throw new ApiError('Failed to create profile', {
                statusCode: 500,
                context: { id: profile.id, error: error.message }
            });
        }

        return data;
    },

    /**
     * Update profile
     */
    async update(userId: string, updates: ProfileUpdate): Promise<ProfileRow> {
        logger.debug('[ProfileRepo] update', { userId, fields: Object.keys(updates) });

        const { data, error } = await supabase
            .from('profiles')
            .update({
                ...updates,
                updated_at: new Date().toISOString(),
            })
            .eq('id', userId)
            .select()
            .single();

        if (error) {
            throw new ApiError('Failed to update profile', {
                statusCode: 500,
                context: { userId, error: error.message },
            });
        }

        return data;
    },

    /**
     * Check if profile exists
     */
    async exists(userId: string): Promise<boolean> {
        const { count, error } = await supabase
            .from('profiles')
            .select('*', { count: 'exact', head: true })
            .eq('id', userId);

        if (error) {
            logger.error('[ProfileRepo] exists check failed', error);
            return false;
        }

        return (count ?? 0) > 0;
    },
};
